import { apiFetch } from "@shared/api/api";
import { ApiError } from "@shared/type/ApiType";
import {
  global,
  globalMenu,
  onSearchStore,
  useMenuStore,
} from "@shared/libs/Global";
import { aiSignal } from "@shared/libs/aiSignal";
import { chatRoomStore } from "@shared/libs/chatRoomStore";
import type { ChatScreenOpen } from "@shared/libs/aiScreenOpen";
import { DESIGNER_MENU, flushDesignDraft, notifyDesignChanged } from "@shared/api/designApi";
import { cleanChatScreenData } from "@shared/utils/ChatScreenDataUtils";
import {
  ChatScreenData,
  ChatScreenColumn,
  ChatAddRowRequest,
  ChatFieldPick,
  ChatScreenDataRow,
  ChatScreenSelection,
  ChatSearchFormData,
} from "@shared/type/CommonType";

const MAX_SELECTED_ROWS = 5;
const MAX_VISIBLE_ROWS = 1000;
const MAX_ROW_FIELDS = 40;
const MAX_TEXT_LENGTH = 120;

export interface ChatReply {
  message: string;
  screenContext?: {
    received: boolean;
    fields: ChatScreenContext;
  };
  screenAction?: ChatScreenAction;
  chart?: ChatChartPayload;
  table?: ChatTablePayload;
  /** 히스토리 레코드 id — 이 답변에 👍/👎를 보낼 때 쓴다(v0.5.9). 저장 실패 시 없을 수 있음. */
  noChat?: string;
  /** 이 답을 만든 에이전트(부품). 프론트가 아이콘+이름 배지로 표시(v0.6.0). */
  agent?: ChatAgentInfo;
  /** 생성 파일(PPT 보고서 등) — url로 즉시 다운로드한다(v0.8.x). */
  file?: ChatFilePayload;
  /**
   * 데이터 소스 판별자(첨부 v1) — "SCREEN" | "ATTACHMENT" | 없음(기존 응답).
   * 보안 판별자: 미지의 non-null 값이면 렌더·액션 실행 전에 거부해야 한다(draft §66).
   */
  sourceKind?: string;
  /** 되묻기(첨부 v1: TABLE_SELECTION 등) — kind 없으면 기존 텍스트 후속 되묻기. */
  clarification?: ChatClarificationPayload;
  /** 문서(IDP) 답 메타(plan §Task 23) — 값 없는 안전 메타(연산/품질/완전성/라인 페이징). */
  document?: ChatDocumentAnswerMeta;
  /** AI Center 경로 전용 — 대화가 길어져 압축이 필요하다는 신호(2026-09-11). */
  compactNeeded?: boolean;
  /** AI Center 경로 전용 — 이 답의 호출 id. 있으면 v2 평가 버튼을 그린다(2026-09-12). */
  idCall?: string;
  /** AI Center 경로 전용 — 이 답에 쓴 포인트. 시간에 마우스를 올리면 보인다(2026-10-21) */
  point?: number;
  /** AI Center 경로 전용 — 이 답이 저장된 턴 번호. 말풍선 태그에 쓴다(2026-10-22) */
  turnSeq?: number;
  /** AI Center 경로 전용 — 다른 메뉴 열기 지시(OPEN_MENU). v1 은 채우지 않는다(2026-09-13). */
  screenOpen?: ChatScreenOpen;
  /** AI Center 경로 전용 — 메뉴 후보가 여럿(PICK_MENU). 챗봇이 번호 버튼을 띄운다(2026-10-06). */
  screenPick?: ChatFieldPick;
  /** AI Center 경로 전용 — 사용자가 중지한 턴(2026-10-14). 화면은 중지 요청 때 이미 말풍선을 붙였다 */
  stopped?: boolean;
}

/** 문서 답 메타 — 페이징은 서버가 돌려준 offset/nextOffset만 신뢰한다. */
export interface ChatDocumentAnswerMeta {
  operation: string;
  documentQuality: "COMPLETE" | "REVIEW_REQUIRED";
  lineCompleteness: "VERIFIED_LOCAL" | "UNKNOWN_RASTER_LINE_COMPLETENESS";
  offset: number;
  total: number;
  hasMore: boolean;
  nextOffset?: number | null;
}

export interface ChatClarificationPayload {
  originalMessage?: string;
  /** 라벨 → 값(TABLE_SELECTION은 라벨 → 불투명 optionId, 삽입 순서 보존). */
  options?: Record<string, string>;
  kind?: string;
}

/** 업로드 파일 참조 — 챗 요청에 실리는 유일한 업로드 페이로드(파일 바이트·헤더 금지). */
export interface ChatAttachmentRef {
  attachmentId: string;
  tableId?: string;
}

/** 선택 전용 전송(시트 선택 클릭) — 메시지와 공존 금지. */
export interface ChatClarificationSelection {
  kind: string;
  optionId: string;
}

export interface ChatAttachmentTableInfo {
  tableId: string;
  label: string;
  rowCount: number;
  columnCount: number;
}

/** 업로드 성공 응답(draft §75) — 파일명·헤더·값은 서버가 절대 에코하지 않는다. */
export interface ChatAttachmentUploadResult {
  attachmentId: string;
  selectedTableId?: string;
  tables: ChatAttachmentTableInfo[];
  slidingTtlSeconds: number;
  hardTtlSeconds: number;
}

/** 챗봇이 생성한 파일의 다운로드 안내(v0.8.x). */
export interface ChatFilePayload {
  fileName: string;
  url: string;
}

/** 응답을 만든 에이전트. model은 규칙엔진 에이전트면 없음(LLM 미사용). */
export interface ChatAgentInfo {
  id: string;
  label: string;
  icon: string;
  model?: string;
}

export type ChatFeedback = "GOOD" | "BAD";

export interface ChatTablePayload {
  title?: string;
  columns: string[];
  rows: string[][];
  basis?: string;
}

export interface ChatScreenAction {
  type: "APPLY_SEARCH_AND_RUN" | "CALL_SCREEN_FUNCTION" | "CLOSE_MENU" | string;
  /** APPLY_SEARCH_AND_RUN: 검색조건 / CALL_SCREEN_FUNCTION: { function, request }(행추가 v2) */
  paramsPatch?:
    | ChatSearchFormData
    | { function?: string; request?: ChatAddRowRequest };
  /** APPLY_SEARCH_AND_RUN: 조회조건 초기화(↻) 먼저 — 중앙 예약 키 _reset (2026-10-16) */
  reset?: boolean;
  /** APPLY_SEARCH_AND_RUN: false 면 조건만 넣고 조회하지 않는다 — 중앙 예약 키 _run */
  run?: boolean;
}

export interface ChatChartPayload {
  type: "bar" | "line" | "pie" | "donut" | string;
  title?: string;
  categoryKey: "label" | string;
  valueKey: "value" | string;
  valueLabel?: string;
  basis?: string;
  data: ChatChartDataPoint[];
}

export interface ChatChartDataPoint {
  label: string;
  value: number;
}

export interface ChatScreenContext {
  path?: string;
  basePath?: string;
  menuCode?: string;
  menuName?: string;
  selection?: ChatScreenSelection;
  search?: ChatSearchFormData;
  selectedRows?: ChatScreenDataRow[];
  visibleRows?: ChatScreenDataRow[];
  visibleColumns?: ChatScreenColumn[];
  grids?: ChatScreenData["grids"];
  /** 현재 조회된 실제 행수(캡 이전) — 건수 답변용. */
  rowCount?: number;
  /** 활성 화면이 챗봇 행추가(onChatAddRow)를 지원하는지 — /도움·라우팅용 런타임 신호. */
  supportsAddRow?: boolean;
  /** 화면이 선언한 행추가 PK 필드(lineKey.fk) — copyByKey 기준 건 식별용 런타임 메타(2026-09-01). */
  addRowKeyField?: string;
}

export async function sendChatMessage(
  message: string,
  extras?: {
    attachmentRef?: ChatAttachmentRef;
    clarificationSelection?: ChatClarificationSelection;
    documentFollowUp?: ChatDocumentFollowUp;
  }
) {
  return apiFetch<ChatReply>("/api/chat", {
    method: "POST",
    headers: {
      "X-TENANT-ID": global.getState().userInfo?.tenant_id || "DEFAULT",
    },
    body: {
      message,
      screenContext: getCurrentChatScreenContext(),
      ...(extras?.attachmentRef ? { attachmentRef: extras.attachmentRef } : {}),
      ...(extras?.clarificationSelection
        ? { clarificationSelection: extras.clarificationSelection }
        : {}),
      ...(extras?.documentFollowUp
        ? { documentFollowUp: extras.documentFollowUp }
        : {}),
    } as unknown as BodyInit,
  });
}

// ══════════════════════════ 문서(IDP) 첨부 계약(plan §Task 21) ══════════════════════════

/** capability 응답 — 문서 형식/한도/고정 고지문. disabledReason 있으면 선택 UI 자체 비활성. */
export interface ChatDocumentCapability {
  enabled: boolean;
  extensions: string[];
  maxBytesByExtension: Record<string, number>;
  maxPages: number;
  maxExtractedLines: number;
  maxLinesPerFourPageBatch: number;
  disclosureKo: string;
  disabledReason?: "SERVICE_UNAVAILABLE" | null;
}

export interface ChatAttachmentCapabilities {
  table: { enabled: boolean; extensions: string[]; maxBytes: number };
  document: ChatDocumentCapability;
}

export type ChatDocumentPhase = "QUEUED" | "INSPECTING" | "EXTRACTING" | "VALIDATING";

/** 202 수락 응답(bare body — ApiResponse 봉투 아님). */
export interface ChatDocumentUploadAccepted {
  attachmentId: string;
  noChat: string;
  contentKind: "DOCUMENT";
  state: "PROCESSING";
  phase: "QUEUED";
  processedPages: number;
  pollAfterMs: number;
  queueDeadline: string;
}

export interface ChatDocumentFailure {
  code: string;
  messageKo: string;
  recoverable: boolean;
}

/** 상태 합집합 — state 판별자. 미지 state는 렌더 전에 거부해야 한다. */
export type ChatDocumentStatus =
  | {
      state: "PROCESSING";
      attachmentId: string;
      contentKind: "DOCUMENT";
      phase: ChatDocumentPhase;
      processedPages: number;
      totalPages?: number | null;
      pollAfterMs: number;
      queueDeadline?: string | null;
      processingDeadline?: string | null;
    }
  | {
      state: "READY";
      attachmentId: string;
      contentKind: "DOCUMENT";
      processedPages: number;
      totalPages: number;
      documentType: "CUSTOMER_PURCHASE_ORDER";
      schemaVersion: string;
      documentQuality: "COMPLETE" | "REVIEW_REQUIRED";
      lineCompleteness: "VERIFIED_LOCAL" | "UNKNOWN_RASTER_LINE_COMPLETENESS";
      expiresAt: string;
      hardExpiresAt: string;
    }
  | {
      state: "FAILED";
      attachmentId: string;
      contentKind: "DOCUMENT";
      processedPages: number;
      totalPages?: number | null;
      error: ChatDocumentFailure;
      expiresAt: string;
    };

/** 문서 후속 합집합 — PAGE는 서버가 돌려준 offset만, CLARIFICATION은 ID만 싣는다. */
export type ChatDocumentFollowUp =
  | { kind: "PAGE"; operation: "LINE_LIST"; offset: number }
  | { kind: "CLARIFICATION"; questionId: string; candidateId: string };

/** 문서 엔드포인트 오류 — 수락 전 {code,messageKo,recoverable} 또는 균일 410. */
export class ChatDocumentRequestError extends Error {
  readonly httpStatus: number;
  readonly code: string;
  readonly recoverable: boolean;

  constructor(httpStatus: number, code: string, messageKo: string, recoverable: boolean) {
    super(messageKo);
    this.httpStatus = httpStatus;
    this.code = code;
    this.recoverable = recoverable;
  }
}

const DOCUMENT_JSON_HEADERS: Record<string, string> = {
  Accept: "application/json",
  "Content-Type": "application/json",
};

/**
 * 문서 엔드포인트 전용 fetch — bare JSON 본문(봉투 없음), 공통 X-* 헤더 미부착
 * (문서 경로는 소유/테넌트/파일명 헤더가 금지 집합이다 — 소유는 인증 세션에서만).
 */
async function documentFetch<T>(
  endpoint: string,
  init: RequestInit,
  expectedStatus: number
): Promise<T> {
  // 테넌트 헤더(2026-09-03): TenantFilter가 화이트리스트 밖 URI에서 이 헤더 없으면 403("접근 권한이 없습니다").
  // apiFetch는 기본으로 붙이지만 문서 경로는 raw fetch라 직접 넣는다. 호출별 headers보다 앞에 두어 덮어쓰기 허용.
  const response = await fetch(`/snotes${endpoint}`, {
    credentials: "include",
    ...init,
    headers: {
      "X-TENANT-ID": global.getState().userInfo?.tenant_id || "DEFAULT",
      ...(init.headers as Record<string, string> | undefined),
    },
  });
  let json: unknown = null;
  try {
    json = await response.json();
  } catch {
    json = null;
  }
  if (response.status !== expectedStatus) {
    const body = (json ?? {}) as {
      code?: unknown;
      messageKo?: unknown;
      recoverable?: unknown;
      message?: unknown;
    };
    throw new ChatDocumentRequestError(
      response.status,
      typeof body.code === "string" ? body.code : String(body.code ?? response.status),
      typeof body.messageKo === "string"
        ? body.messageKo
        : typeof body.message === "string"
          ? body.message
          : "문서 요청 처리 중 오류가 발생했습니다.",
      body.recoverable === true
    );
  }
  return json as T;
}

/** capability 조회 — 기존 ApiResponse 봉투 경로(GET). */
export async function fetchChatAttachmentCapabilities() {
  return apiFetch<ChatAttachmentCapabilities>("/api/chat/attachments/capabilities", {
    method: "GET",
  });
}

/**
 * 문서 업로드 — 브라우저 File을 원시 본문으로 전송한다. 파일명·소유·회사 헤더는 절대 싣지
 * 않는다(파일명은 로컬 UI 전용). Content-Type은 정확히 application/octet-stream 하나.
 */
export async function uploadChatDocument(file: File, signal?: AbortSignal) {
  return documentFetch<ChatDocumentUploadAccepted>(
    "/api/chat/attachments/documents",
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/octet-stream",
      },
      body: file,
      signal,
    },
    202
  );
}

/** 상태 폴링 — id는 본문으로(URL/쿼리 미노출). */
const DOCUMENT_PHASES: ReadonlySet<string> = new Set(["QUEUED", "INSPECTING", "EXTRACTING", "VALIDATING"]);
const POLL_AFTER_MIN_MS = 250;
const POLL_AFTER_MAX_MS = 60_000;

function malformedStatus(): ChatDocumentRequestError {
  return new ChatDocumentRequestError(200, "STATUS_MALFORMED", "문서 상태 응답이 올바르지 않습니다.", true);
}

/**
 * 상태 응답 런타임 검증(리뷰 P1-5) — HTTP 경계에서 상태별 필수 필드와 pollAfterMs 범위를 확인한다.
 * 타입 단언만으로는 잘못된 PROCESSING 응답(pollAfterMs 누락)이 setTimeout(undefined)로 즉시 재폴링을 반복한다.
 */
export function assertDocumentStatus(json: unknown): ChatDocumentStatus {
  if (!json || typeof json !== "object") {
    throw malformedStatus();
  }
  const body = json as Record<string, unknown>;
  if (typeof body.attachmentId !== "string" || body.attachmentId.length === 0) {
    throw malformedStatus();
  }
  switch (body.state) {
    case "PROCESSING": {
      if (
        typeof body.phase !== "string"
        || !DOCUMENT_PHASES.has(body.phase)
        || typeof body.processedPages !== "number"
        || typeof body.pollAfterMs !== "number"
        || !Number.isFinite(body.pollAfterMs)
        || body.pollAfterMs < POLL_AFTER_MIN_MS
        || body.pollAfterMs > POLL_AFTER_MAX_MS
      ) {
        throw malformedStatus();
      }
      return body as unknown as ChatDocumentStatus;
    }
    case "READY": {
      if (
        typeof body.totalPages !== "number"
        || (body.documentQuality !== "COMPLETE" && body.documentQuality !== "REVIEW_REQUIRED")
        || (body.lineCompleteness !== "VERIFIED_LOCAL"
          && body.lineCompleteness !== "UNKNOWN_RASTER_LINE_COMPLETENESS")
        || typeof body.expiresAt !== "string"
        || typeof body.hardExpiresAt !== "string"
      ) {
        throw malformedStatus();
      }
      return body as unknown as ChatDocumentStatus;
    }
    case "FAILED": {
      const error = body.error as Record<string, unknown> | undefined;
      if (!error || typeof error !== "object" || typeof error.messageKo !== "string") {
        throw malformedStatus();
      }
      return body as unknown as ChatDocumentStatus;
    }
    default:
      throw malformedStatus();
  }
}

export async function fetchChatDocumentStatus(attachmentId: string, signal?: AbortSignal) {
  return assertDocumentStatus(await documentFetch<unknown>(
    "/api/chat/attachments/documents/status",
    {
      method: "POST",
      headers: DOCUMENT_JSON_HEADERS,
      body: JSON.stringify({ attachmentId }),
      signal,
    },
    200
  ));
}

/**
 * 표 파일 업로드(첨부 v1) — 전송 파일명은 원본이 아니라 규약명 upload.xlsx|upload.csv
 * (원본 파일명은 브라우저 메모리에만, draft §22). 실제 바이트 검증은 서버 파서 소관.
 */
export async function uploadChatAttachment(
  file: File,
  kind: "xlsx" | "csv",
  signal?: AbortSignal
) {
  const form = new FormData();
  form.append("file", file, `upload.${kind}`);
  return apiFetch<ChatAttachmentUploadResult>("/api/chat/attachments", {
    method: "POST",
    body: form,
    signal,
  });
}

/** 첨부 삭제 — id는 URL이 아니라 본문으로(접근로그 URL 미노출, draft §46). */
export async function deleteChatAttachment(attachmentId: string) {
  return apiFetch<void>("/api/chat/attachments", {
    method: "DELETE",
    body: { attachmentId } as unknown as BodyInit,
  });
}

/**
 * 답변 피드백 전송(v0.5.9 파일럿 수집). 수집용 부가 기능이라 실패해도 대화 흐름을 막지 않는다.
 * 같은 noChat에 다시 보내면 마지막 값으로 갱신된다(사용자가 마음을 바꿔도 됨).
 */
/** 대화 맥락 초기화(v0.8.2) — 채팅 클리어 시 서버의 멀티턴 버퍼도 함께 비운다. */
export async function clearChatContext() {
  return apiFetch<number>("/api/chat/context/clear", {
    method: "POST",
    headers: {
      "X-TENANT-ID": global.getState().userInfo?.tenant_id || "DEFAULT",
    },
    body: {} as unknown as BodyInit,
  });
}

export async function sendChatFeedback(
  noChat: string,
  feedback: ChatFeedback,
  comment?: string
) {
  return apiFetch<boolean>(`/api/chat/${noChat}/feedback`, {
    method: "POST",
    headers: {
      "X-TENANT-ID": global.getState().userInfo?.tenant_id || "DEFAULT",
    },
    body: {
      feedback,
      ...(comment ? { comment } : {}),
    } as unknown as BodyInit,
  });
}

/**
 * 지금 열려 있는 화면의 메뉴 코드. 탭이 없으면 undefined.
 * 활성 메뉴 정보는 탭을 다 닫아도 마지막 메뉴로 남는다 — 그걸 그대로 보내면 중앙이 그 화면이 열린 줄 알고
 * "지금 화면에서 조회" 지시를 내려 화면이 "검색 패널이 준비되지 않았습니다" 로 멈춘다 (2026-10-06 홈에서 시험).
 */
function openScreenMenuCode(): string | undefined {
  const code = globalMenu.getState().activeMenuInfo?.cd_menu?.trim();
  if (!code) return undefined;
  const tabs = onSearchStore.getState().onSearchInfo.menuData || [];
  return tabs.some((tab) => tab?.cd_menu === code) ? code : undefined;
}

function getCurrentChatScreenContext(): ChatScreenContext {
  const path = window.location.pathname;

  const activeMenuInfo = globalMenu.getState().activeMenuInfo;
  const menuCode = openScreenMenuCode();

  if (!menuCode) {
    return { path };
  }

  const selection = getActiveChatScreenSelection(menuCode);
  const search = getActiveSearchFormData();
  const screenData = getActiveChatScreenData();
  // 화면이 선언한 행추가 PK(lineKey.fk) — 백엔드는 이 값만 쓰고 추론하지 않는다.
  const addRowKeyField = onSearchStore.getState().chatAddRowKeyField();
  return {
    path,
    basePath: menuCode ? `/menu/${menuCode}` : path,
    menuCode,
    menuName: activeMenuInfo?.nm_menu,
    ...(selection ? { selection } : {}),
    ...(search ? { search } : {}),
    ...(screenData?.selectedRows ? { selectedRows: screenData.selectedRows } : {}),
    ...(screenData?.visibleRows ? { visibleRows: screenData.visibleRows } : {}),
    ...(screenData?.visibleColumns ? { visibleColumns: screenData.visibleColumns } : {}),
    ...(screenData?.grids ? { grids: screenData.grids } : {}),
    ...(screenData?.rowCount !== undefined ? { rowCount: screenData.rowCount } : {}),
    // 행추가 v2 지원 신호 — 활성 화면 ref의 onChatAddRow 유무(런타임 사실, DB 아님).
    supportsAddRow: onSearchStore.getState().supportsChatAddRow(),
    ...(addRowKeyField ? { addRowKeyField } : {}),
  };
}

function getActiveSearchFormData(): ChatSearchFormData | undefined {
  const search = onSearchStore.getState().getCurrentSearchFormData();
  if (!search || Object.keys(search).length === 0) {
    return undefined;
  }
  return search;
}

function getActiveChatScreenSelection(
  menuCode: string | undefined
): ChatScreenSelection | undefined {
  if (!menuCode) {
    return undefined;
  }

  const selection = globalMenu.getState().chatScreenSelections[menuCode];
  if (!selection || Object.keys(selection).length === 0) {
    return undefined;
  }

  return selection;
}

function getActiveChatScreenData(): ChatScreenData | undefined {
  const screenData = onSearchStore.getState().getCurrentChatScreenData();
  return cleanChatScreenData(screenData, {
    maxSelectedRows: MAX_SELECTED_ROWS,
    maxVisibleRows: MAX_VISIBLE_ROWS,
    maxFields: MAX_ROW_FIELDS,
    maxTextLength: MAX_TEXT_LENGTH,
  });
}

/* ==================================================================
 * 새 엔진(AI Center) 경로 — 2026-09-11
 *
 * 기존 sendChatMessage 는 손대지 않는다. 위 함수와 아래 함수가 나란히 산다.
 * 챗봇 화면의 엔진 콤보가 둘 중 하나를 고른다.
 * 비교 테스트가 끝나면 진 쪽을 통째로 지운다. 그래서 섞지 않는다.
 * ================================================================== */

/** 챗봇 엔진. 대화 단위로 고정된다 */
export type ChatEngine = "V1" | "V2";

export const CHAT_ENGINE_OPTIONS: { value: ChatEngine; label: string }[] = [
  { value: "V1", label: "레거시" },
  { value: "V2", label: "AI Center" },
];

/**
 * 새 엔진 응답 원형.
 * 대화 저장이 거래처(우리) BE 에 있어 대화방 id 를 우리가 들고 다닌다.
 */
interface AiGatewayResponse {
  idConv: string;
  /** 이 답의 호출 id. 평가 · 행동 신호가 이것으로 답을 가리킨다 */
  idCall?: string | null;
  text: string;
  /** 답변근거 SCREEN / TOOL / NONE */
  tpBasis?: string;
  /** 화면 기준일 때 그 화면의 조회시각 */
  tmScreen?: string;
  /** 화면 조작 지시. 중앙 모양. toChatReply 가 화면이 실행하는 모양으로 바꾼다 */
  screenActions?: { tpAction?: string; cdMenu?: string; paramsJson?: string }[];
  qtPoint?: number;
  /** 답한 역할 (2026-10-22) — SEARCH · INPUT · ANALYZE · REPORT · GUIDE. 없으면 일반 답 */
  cdRole?: string | null;
  /** 이 답이 저장된 턴 번호 (2026-10-22) — 말풍선 태그에 쓴다 */
  noSeq?: number | null;
  compactNeeded?: boolean;
  /** 집계 Tool 이 만든 차트 · 표. v1 과 같은 모양이라 그대로 넘긴다 */
  chart?: ChatChartPayload | null;
  table?: ChatTablePayload | null;
  /** 메뉴디자이너 제안 (2026-09-29). BE 가 초안에 저장한 뒤 내려준다. 있으면 디자이너 화면에 다시 읽으라고 알린다 */
  designProposal?: unknown;
  /** 답변 중지로 끝난 턴 (aibot-v2-chat-cancel, 2026-10-14) */
  stopped?: boolean;
  /** 보고서 PPT (aibot-v2-screen-report, 2026-10-16). v1 과 같은 모양 — 받자마자 내려받는다 */
  file?: ChatFilePayload | null;
}

/**
 * 새 엔진의 대화방 id 는 chatRoomStore 가 든다 (2026-10-07, aibot-v2-conversation-rooms).
 * 전에는 이 모듈의 변수 하나였다. 그러면 A 방에 보낸 답이 늦게 돌아왔을 때 그 사이 고른 B 방에 붙는다.
 * 보내는 쪽이 전송 직전에 방 id 를 잡아 넘기고, 돌아온 뒤 아직 그 방인지 확인한다.
 */

/** 새 대화를 시작한다. 대화 지우기와 엔진 변경에서 부른다 */
export function resetChatEngineV2Conversation() {
  chatRoomStore.getState().newRoom();
}

/**
 * 이 사용자가 열 수 있는 메뉴 — 메뉴 목록에 있는 메뉴 전부.
 * fg_select 로 거르지 않는다 — 메뉴 권한이 아니라 조회 데이터 범위(SYS006 1 회사 · 2 사업장 …)다.
 * 'Y' 만 담았더니 값이 1 · 2 … 인 실제 메뉴가 빠져 26개만 갔다(2026-10-13 "수주" 후보가 하나뿐). BE 가 한 번 더 확인한다
 */
function getAllowedMenuCodes(): string[] {
  try {
    const codes = useMenuStore
      .getState()
      .menuItems.filter((menu) => menu.cd_menu)
      .map((menu) => menu.cd_menu);
    return Array.from(new Set(codes));
  } catch {
    return [];
  }
}

/**
 * 새 엔진으로 한 마디 보낸다.
 *
 * 화면정보는 기존 엔진과 같은 함수로 모은다(여기서 1000행까지 자른다).
 * 최종적으로 무엇을 남기고 무엇을 가릴지는 BE 가 정한다(50행 + 마스킹).
 * 프론트 상한만 믿으면 브라우저를 고쳐 우회할 수 있다.
 *
 * 첨부·되묻기·문서 후속은 아직 이 경로에 없다. 기존 엔진 전용이다.
 * 챗봇 화면이 새 엔진일 때 첨부 버튼을 막는다.
 */
export interface ChatReplyV2 {
  data: ChatReply;
  /** 서버가 준 대화방 id. 새 방이면 여기서 처음 온다 */
  idConv: string | null;
  /**
   * 화면 밖 효과(행동 신호 기억 · 디자이너 알림). 보내는 쪽이 "아직 그 방인가" 를 확인한 뒤에만 부른다.
   * 늦게 돌아온 A 방의 답이 B 방이나 B 화면에 아무 효과도 내지 않게 하려는 것이다
   */
  /** 디자이너 알림. 제안은 서버 초안에 이미 저장됐다 — 화면 조작 성공 여부와 무관하게 알린다 */
  notifyDesign: () => void;
  /**
   * 행동 신호 기억("AI 가 이 조건을 채웠다" / "답이 있었다"). 화면 조작이 실제로 성공한 뒤에만 부른다 —
   * 실패한 조작을 기억해 두면 이후 사용자의 수동 조회가 "AI 조건 수정" 으로 잘못 집계된다 (2026-10-08)
   */
  rememberSignals: () => void;
  /**
   * 보낸 뒤 화면이 바뀌었는가 — 메뉴와 선택 상태(선택 행 · 화면 선택값)까지 본다. 바뀌었으면 조회 · 행추가 · 메뉴 열기를 하지 않는다.
   * 같은 메뉴라도 선택 행이 바뀌었으면 행추가가 다른 행 기준으로 돈다 (2026-10-08 2차 리뷰)
   */
  screenChanged: () => boolean;
  /** 메뉴만 비교. 우리 쪽 화면 조작(조회 적용) 뒤 재확인용 — 그 조작이 바꾼 선택 상태는 탓하지 않는다 */
  menuChanged: () => boolean;
}

/**
 * 화면 지문 — 메뉴, 화면 선택값, 선택 행, 검색조건, 건수, 보이는 행 전체.
 * 중앙에 보낸 화면 정보와 같은 재료다. 조건을 바꿔 다시 조회하거나 어느 행 값이 바뀌어도 다른 화면으로 본다.
 * 앞 3행만 보면 네 번째 행 금액이 바뀐 것을 놓친다 (2026-10-08 리뷰). 보이는 행은 이미 1000행까지 정리된 값이라
 * 답이 올 때 한 번 문자열로 만드는 비용은 작다. 공용 스토어에 버전 번호를 더하는 것보다 영향 범위가 좁다
 */
function screenFingerprint(): string {
  const menu = openScreenMenuCode() || "";
  if (!menu) return "";
  const selection = getActiveChatScreenSelection(menu) ?? null;
  const data = getActiveChatScreenData();
  const search = getActiveSearchFormData() ?? null;
  try {
    return JSON.stringify([
      menu,
      selection,
      data?.selectedRows ?? null,
      search,
      data?.rowCount ?? null,
      data?.visibleRows ?? null,
    ]);
  } catch {
    return menu;
  }
}

/* ───────────────────────── V2 첨부 (aibot-v2-attachments 0단계, 2026-10-26) ───────────────────────── */

export type ChatAttachmentKindV2 = "TEXT" | "IMAGE" | "DOCUMENT" | "TABLE";

/** 질문에 싣는 첨부 참조 — TEXT 는 글을 인라인으로, 그림 · 파일은 업로드가 준 id 로 */
export interface ChatAttachmentRefV2 {
  id?: string;
  kind: ChatAttachmentKindV2;
  name: string;
  text?: string;
}

/** 업로드 · 확인 응답 — 화면이 쓰는 것만(id · 정규화된 크기). 바이트는 없다 */
export interface ChatAttachmentViewV2 {
  id: string;
  size: number;
  /** IMAGE · DOCUMENT */
  kind?: ChatAttachmentKindV2;
  /** PDF 쪽 수 (2026-10-28) */
  pages?: number | null;
}

const V2_ATTACHMENT_PATH = "/api/chat/v2/attachments";

/** 그림 · PDF 업로드 — 서버가 검사 · 정규화해 암호화 보관하고 id 를 준다. 질문을 보내면 그 방 · 턴에 묶인다 (2026-10-27, PDF 2026-10-28) */
export async function uploadChatAttachmentV2(file: File): Promise<ChatAttachmentViewV2> {
  const form = new FormData();
  form.append("file", file, file.name);
  const res = await apiFetch<ChatAttachmentViewV2>(V2_ATTACHMENT_PATH, { method: "POST", body: form });
  if (!res.data) throw new Error("그림을 올리지 못했습니다.");
  return res.data;
}

/** 첨부 삭제 — 없어도 조용히 */
export async function deleteChatAttachmentV2(id: string): Promise<void> {
  await apiFetch<boolean>(`${V2_ATTACHMENT_PATH}/${encodeURIComponent(id)}`, { method: "DELETE" });
}

/** 살아 있나 — 다시 시도 전에 본다. 만료(410)면 null */
export async function checkChatAttachmentV2(id: string): Promise<ChatAttachmentViewV2 | null> {
  try {
    const res = await apiFetch<ChatAttachmentViewV2>(`${V2_ATTACHMENT_PATH}/${encodeURIComponent(id)}`);
    return res.data ?? null;
  } catch (err) {
    if (err instanceof ApiError && err.code === 410) return null;
    throw err;
  }
}

export async function sendChatMessageV2(
  message: string,
  idConv: string | null,
  /** retry: "다시 시도" — 직전 턴과 같은 질문. BE 가 직전 턴을 맥락에서 빼고 새 턴으로 저장한다 (2026-10-14).
      idRequest: 화면이 만든 요청 ID(UUID) — 답변 중지(cancelChatV2)가 이것으로 요청을 가리킨다 (aibot-v2-chat-cancel).
      quote: 답장한 앞 답 원문(카톡 답장, 2026-10-15). 있으면 중앙이 고르기 모델을 건너뛰고 AI 가 이것과 함께 본다.
      screenOnly: 지금 화면으로 답하라는 표시(2026-10-21) — [조회하고 답하기] 뒤 다시 묻기 · [지금 화면으로 답하기]
      attachments: 첨부 참조(2026-10-26) — message 는 입력한 글만, 긴 글 · 그림은 여기로. BE 가 합치고 중앙엔 조각으로 보낸다 */
  opts: {
    retry?: boolean;
    idRequest?: string;
    quote?: string;
    screenOnly?: boolean;
    attachments?: ChatAttachmentRefV2[];
  } = {}
): Promise<ChatReplyV2> {
  const cdMenu = openScreenMenuCode() || "";
  /* 보낸 순간의 화면 지문. 답이 왔을 때 이것과 다르면 화면 조작을 하지 않는다 */
  const sentScreen = screenFingerprint();
  /* 메뉴디자이너 화면이 활성이면 그 이슈번호를 같이 보낸다 (2026-09-29). 디자이너 화면이 chatScreenSelections 에 올려 둔다.
     초안 · 호출목적(DRAFT)은 BE 가 채운다. 채팅창은 이 창 하나다 */
  const designNoIssue =
    cdMenu === DESIGNER_MENU
      ? String(globalMenu.getState().chatScreenSelections[cdMenu]?.no_issue ?? "").trim()
      : "";
  /* 디자이너 화면에 안 저장된 편집이 있으면 먼저 저장 — AI 가 최신 초안을 보게 */
  if (designNoIssue) {
    await flushDesignDraft(designNoIssue);
  }

  const response = await apiFetch<AiGatewayResponse>("/api/chat/v2", {
    method: "POST",
    body: {
      idConv,
      question: message,
      cdMenu,
      screenContext: getCurrentChatScreenContext(),
      allowedMenus: getAllowedMenuCodes(),
      ...(designNoIssue ? { design: { noIssue: designNoIssue } } : {}),
      ...(opts.retry ? { retry: true } : {}),
      ...(opts.idRequest ? { idRequest: opts.idRequest } : {}),
      ...(opts.quote ? { quote: opts.quote } : {}),
      ...(opts.screenOnly ? { screenOnly: true } : {}),
      ...(opts.attachments && opts.attachments.length > 0 ? { attachments: opts.attachments } : {}),
    } as unknown as BodyInit,
  });

  /* 화면 조작 대상 메뉴는 보낸 순간의 화면으로 정한다 — 답이 온 순간의 화면이 아니다 (2026-10-08) */
  const mapped = toChatReply(response.data, cdMenu || undefined);
  return {
    data: mapped.reply,
    idConv: response.data?.idConv ?? null,
    screenChanged: () => screenFingerprint() !== sentScreen,
    menuChanged: () => (openScreenMenuCode() || "") !== cdMenu,
    rememberSignals: () => mapped.remember(),
    notifyDesign: () => {
      /* 제안이 왔다 — BE 가 초안에 저장했다. 디자이너 화면이 다시 읽는다 */
      if (designNoIssue && response.data?.designProposal) {
        notifyDesignChanged(designNoIssue);
      }
    },
  };
}

/**
 * 대화 압축을 요청한다.
 *
 * 답을 화면에 보여준 뒤에 부른다. 같이 돌리면 사용자가 그만큼 더 기다린다.
 * 압축도 LLM 을 부르는 일이라 시간이 걸린다.
 *
 * 압축할 것이 없으면 done=false 로 조용히 돌아온다. 오류가 아니다.
 * 실패해도 대화는 그대로 이어진다. 다음 질문에서 다시 시도된다.
 */
export async function compactChatV2(idConv: string | null, manual = false) {
  if (!idConv) {
    return null;
  }
  const response = await apiFetch<{
    done: boolean;
    notice?: string | null;
    compactIndex?: string[];
  }>("/api/chat/v2/compact", {
    method: "POST",
    body: { idConv, manual } as unknown as BodyInit,
  });
  return response.data ?? null;
}

/**
 * 새 엔진 응답을 화면이 아는 형태로 바꾼다.
 *
 * 화면조작 지시는 여기서만 모양을 맞춘다. handleScreenReply 는 손대지 않는다.
 *   중앙  { tpAction, cdMenu, paramsJson }
 *   화면  { type, paramsPatch } + screenContext.fields.menuCode
 * menuCode 를 채워야 기존의 메뉴 불일치 검사(MENU_MISMATCH)가 그대로 동작한다.
 * 지금은 APPLY_SEARCH_AND_RUN 하나만 옮긴다. 저장은 없다. 사람이 누른다.
 */
/** 답한 역할 → 말풍선 위 배지 (2026-10-22). 역할은 중앙이 그 답에서 한 일로 정한다. 모르는 값 · 없음은 AI Center */
const ROLE_AGENTS: Record<string, ChatAgentInfo> = {
  SEARCH: { id: "search", label: "조회 도우미", icon: "search" },
  INPUT: { id: "input", label: "입력 도우미", icon: "playlist_add" },
  ANALYZE: { id: "analyze", label: "데이터 분석가", icon: "analytics" },
  REPORT: { id: "report", label: "보고서 도우미", icon: "description" },
  GUIDE: { id: "guide", label: "안내 도우미", icon: "menu_book" },
};

export function agentOfRole(role: string | null | undefined): ChatAgentInfo {
  return (role && ROLE_AGENTS[role]) || { id: "aicenter", label: "AI Center", icon: "smart_toy" };
}

function toChatReply(
  res: AiGatewayResponse | null | undefined,
  /** 보낸 순간 열려 있던 메뉴. 중앙이 메뉴를 안 찍은 조작은 이 화면 대상이다 */
  sentMenu: string | undefined
): { reply: ChatReply; remember: () => void } {
  if (!res) {
    return { reply: { message: "" }, remember: () => undefined };
  }
  const picked = pickScreenActions(res.screenActions, sentMenu);
  const action = picked.action;
  /* 행동 신호용 기억. 조회 지시가 있었으면 어느 조건을 채웠는지, 없었으면 답이 있었다는 것만.
     메뉴 열기에 조건이 실렸으면 그것도 AI 가 채운 조건이다.
     여기서 바로 기억하지 않고 함수로 돌려준다 — 보내는 쪽이 "아직 그 방인가" 를 본 뒤 부른다 (2026-10-07) */
  const idCall = res.idCall || undefined;
  const menuNow = sentMenu;
  const remember = () => {
    /* 넣기만 했으면(조회 안 함) AI 조회가 아니다 — 답이 있었다는 것만 */
    if (action && action.screenAction.type === "APPLY_SEARCH_AND_RUN" && action.screenAction.run !== false) {
      aiSignal.rememberAiSearch(action.menuCode || menuNow, idCall, action.screenAction.paramsPatch);
    } else if (picked.open && picked.open.paramsPatch && Object.keys(picked.open.paramsPatch).length > 0) {
      aiSignal.rememberAiSearch(picked.open.menuCode, idCall, picked.open.paramsPatch);
    } else {
      aiSignal.rememberAnswer(menuNow, idCall);
    }
  };
  const reply: ChatReply = {
    idCall,
    message: withBasis(res.text, res.tpBasis, res.tmScreen),
    /* 말풍선 위 배지 — 답한 역할(조회 도우미 등). 없으면 AI Center (2026-10-22) */
    agent: agentOfRole(res.cdRole),
    /* 대화가 길어졌다. 답을 보여준 뒤 압축을 부르라는 신호다 */
    compactNeeded: res.compactNeeded === true,
    ...(typeof res.qtPoint === "number" ? { point: res.qtPoint } : {}),
    ...(typeof res.noSeq === "number" ? { turnSeq: res.noSeq } : {}),
    /* 집계 결과. 모양이 같아 v1 을 그리던 코드가 그대로 그린다 */
    ...(res.chart && Array.isArray(res.chart.data) ? { chart: res.chart } : {}),
    ...(res.table && Array.isArray(res.table.rows) ? { table: res.table } : {}),
    /* 보고서 파일. v1 길(handleScreenReply)이 그대로 내려받는다 */
    ...(res.file?.url ? { file: { fileName: res.file.fileName || "report.pptx", url: res.file.url } } : {}),
    ...(action
      ? {
          screenAction: action.screenAction,
          screenContext: { received: true, fields: { menuCode: action.menuCode } },
        }
      : {}),
    ...(picked.open ? { screenOpen: picked.open } : {}),
    ...(picked.pick ? { screenPick: picked.pick } : {}),
    ...(res.stopped ? { stopped: true } : {}),
  };
  return { reply, remember };
}

/**
 * 답변 중지 (aibot-v2-chat-cancel, 2026-10-14). 보낼 때 쓴 요청 ID 로 거래처 BE 에 중지 표시를 한다.
 * true 면 서버가 중지 턴("답변을 중지했습니다.")을 저장했고, 진행 중이던 호출은 돌아와도 버린다.
 * false 면 이미 답이 났다(곧 화면에 온다) — 기다리면 된다. 옛 BE(404)는 예외로 올라온다.
 */
export async function cancelChatV2(idRequest: string): Promise<boolean> {
  const res = await apiFetch<{ stopped?: boolean }>("/api/chat/v2/cancel", {
    method: "POST",
    body: { idRequest } as unknown as BodyInit,
  });
  return res.data?.stopped === true;
}

/** 요청 ID. 안전한 문맥(https · localhost)이 아니면 crypto.randomUUID 가 없어 흉내 낸다 */
export function newChatRequestId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  } catch {
    /* 아래 흉내 */
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/**
 * 중앙의 화면 조작 지시를 화면 모양으로 바꾼다 (D47 세 종류).
 *   APPLY_SEARCH_AND_RUN · CALL_SCREEN_FUNCTION → screenAction (기존 handleScreenReply 가 실행)
 *   OPEN_MENU                                   → screenOpen   (v2 경로가 aiScreenOpen 으로 실행)
 *   PICK_MENU                                   → screenPick   (챗봇이 번호 버튼, 고르면 aiScreenOpen)
 * 각각 첫 번째 하나만 옮긴다. 조건 JSON 이 깨져 있으면 지시를 버린다 —
 * 엉뚱한 조건으로 조회하는 것보다 안 하는 게 낫다. 메뉴가 비면 지금 활성 화면이다.
 */
function pickScreenActions(actions: AiGatewayResponse["screenActions"], sentMenu: string | undefined): {
  action: { screenAction: ChatScreenAction; menuCode?: string } | null;
  open: ChatScreenOpen | null;
  pick: ChatFieldPick | null;
} {
  let action: { screenAction: ChatScreenAction; menuCode?: string } | null = null;
  let open: ChatScreenOpen | null = null;
  let pick: ChatFieldPick | null = null;
  if (!actions) return { action, open, pick };
  for (const a of actions) {
    if (!a || !a.tpAction) continue;
    const params = parseObject(a.paramsJson);
    if (!params) continue;
    const cdMenu = a.cdMenu && a.cdMenu.trim() ? a.cdMenu.trim() : undefined;
    const menuCode = cdMenu || sentMenu;
    if (a.tpAction === "APPLY_SEARCH_AND_RUN" && !action) {
      /* _reset · _run 은 칸 이름이 아니라 "초기화 먼저" · "조회 여부" 표시 — 떼어 낸다 (2026-10-16) */
      const { _reset, _run, ...conditions } = params as Record<string, unknown>;
      action = {
        screenAction: {
          type: "APPLY_SEARCH_AND_RUN",
          paramsPatch: conditions as ChatSearchFormData,
          reset: _reset === true,
          run: _run !== false,
        },
        menuCode,
      };
    } else if (a.tpAction === "CLOSE_MENU" && !action) {
      /* 메뉴 닫기 — cdMenu 하나 또는 {all:true}. 수정 중 확인 · 남기기는 화면 공통 닫기가 한다 (2026-10-16) */
      action = { screenAction: { type: "CLOSE_MENU", paramsPatch: params as ChatSearchFormData }, menuCode: cdMenu };
    } else if (a.tpAction === "CALL_SCREEN_FUNCTION" && !action) {
      /* 중앙 모양 { function, request } 이 v1 행추가와 같다. 그대로 넘긴다 */
      if (typeof (params as { function?: unknown }).function !== "string") continue;
      action = {
        screenAction: {
          type: "CALL_SCREEN_FUNCTION",
          paramsPatch: params as { function?: string; request?: ChatAddRowRequest },
        },
        menuCode,
      };
    } else if (a.tpAction === "OPEN_MENU" && !open && cdMenu) {
      /* _search 는 칸 이름이 아니라 "조회까지" 표시 — 떼어 낸다 (2026-10-14) */
      const { _search, ...conditions } = params as Record<string, unknown>;
      open = { menuCode: cdMenu, paramsPatch: conditions as ChatSearchFormData, search: _search === true };
    } else if (a.tpAction === "PICK_MENU" && !pick) {
      pick = menuPick(params);
    }
  }
  return { action, open, pick };
}

/**
 * PICK_MENU 의 paramsJson {prompt, candidates:[{code,name,conditions?}], conditions} → 챗봇 후보. 모양이 틀리면 null.
 * 후보마다 conditions 가 있으면 그 메뉴의 칸 이름으로 옮긴 조건이다 — 메뉴마다 기간 칸 이름이 달라서(2026-10-13).
 */
function menuPick(params: Record<string, unknown>): ChatFieldPick | null {
  const raw = Array.isArray(params.candidates) ? (params.candidates as unknown[]) : [];
  const candidates = raw
    .map((c) => (c && typeof c === "object" ? (c as { code?: unknown; name?: unknown; conditions?: unknown }) : null))
    .filter((c): c is { code?: unknown; name?: unknown; conditions?: unknown } => c !== null && typeof c.code === "string" && c.code !== "")
    .map((c) => {
      const own = c.conditions && typeof c.conditions === "object" && !Array.isArray(c.conditions)
        ? (c.conditions as ChatSearchFormData)
        : undefined;
      return {
        code: String(c.code),
        name: typeof c.name === "string" && c.name ? c.name : String(c.code),
        ...(own ? { patch: own } : {}),
      };
    });
  /* 후보 하나는 메뉴 권하기 — 정보를 묻는 말에 중앙이 버튼 하나로 권한다(누르면 열고 조회, 2026-10-15) */
  if (candidates.length < 1) return null;
  const conditions = params.conditions && typeof params.conditions === "object" && !Array.isArray(params.conditions)
    ? (params.conditions as ChatSearchFormData)
    : {};
  return {
    target: "menu",
    label: "메뉴",
    prompt: typeof params.prompt === "string" && params.prompt ? params.prompt : "열 메뉴를 골라 주세요.",
    candidates,
    patch: conditions,
    search: params.search === true,
    /* 조회하고 답하기 — 값을 묻는 말에 중앙이 지금 화면 조회를 권했다. 조회 뒤 이 질문을 다시 보낸다 (2026-10-21) */
    ...(typeof params.ask === "string" && params.ask ? { ask: params.ask } : {}),
  };
}

/** 객체 JSON 만 받는다. 비면 빈 객체. 깨졌거나 객체가 아니면 null */
function parseObject(json: string | undefined): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(json || "{}");
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** 화면 기준으로 답했으면 언제 조회한 화면인지 밝힌다. 낡은 답을 새 답으로 오해하지 않게 */
function withBasis(text: string, tpBasis?: string, tmScreen?: string): string {
  const body = text || "";
  if (tpBasis !== "SCREEN" || !tmScreen) {
    return body;
  }
  return `${body}\n\n_화면 기준 (${tmScreen} 조회)_`;
}

/* ------------------------------------------------------------------ V2 "/도움" (aibot-v2-help, 2026-10-13) */

/** 중앙 HelpResponse 와 같은 모양. key: screen · search · tools · open · more */
export interface ChatHelpSection {
  key: string;
  title: string;
  items: { text: string; note?: string | null }[];
}

/** V2 에서 서버로 보내지 않고 가로채는 명령. 앞뒤 공백만 무시한다 */
export function isChatHelpCommandV2(text: string): boolean {
  return text.trim() === "/도움";
}

/**
 * "/도움" — 지금 이 사용자가 쓸 수 있는 기능을 예시 질문으로 받는다.
 * 재료는 sendChatMessageV2 와 같다(지금 메뉴 · 권한 메뉴). BE 가 권한으로 다시 거르고 중앙이 규칙으로 조립한다.
 * 대화방 · 포인트 · 행동 신호 없음. 중앙이 아직 이 경로를 모르면 BE 가 문구로 실패시킨다.
 */
export async function fetchChatHelpV2(): Promise<ChatHelpSection[]> {
  const response = await apiFetch<{ sections?: ChatHelpSection[] | null }>("/api/chat/v2/help", {
    method: "POST",
    body: {
      cdMenu: openScreenMenuCode() || "",
      allowedMenus: getAllowedMenuCodes(),
    } as unknown as BodyInit,
  });
  return (response.data?.sections ?? []).filter((s) => s && Array.isArray(s.items) && s.items.length > 0);
}

/**
 * AiMarkdown 이 해석하는 것만 무력화한다 — 역슬래시 이스케이프는 그 렌더러가 풀지 않아 그대로 보인다 (2026-10-13 계획 검토 2).
 * 인라인은 **굵게** 와 `코드` 뿐이고, 줄 단위 문법(- · | · # · ```)은 항목 안 줄바꿈을 없애면 닿지 않는다.
 * _ · [] 같은 글자는 그 렌더러가 특수문자로 보지 않으니 손대지 않는다.
 */
function plainInline(text: string): string {
  return (text || "")
    .replace(/\s+/g, " ")
    .replace(/\*\*/g, "＊＊")
    .replace(/`/g, "'")
    .trim();
}

/** 섹션 → 말풍선 하나(제목 굵게 + 목록). AiMarkdown 으로 그린다 */
export function helpSectionsToMarkdown(sections: ChatHelpSection[]): string {
  if (!sections.length) {
    return "지금 보여 드릴 예시가 없습니다. 메뉴를 열거나 권한을 확인한 뒤 다시 입력해 주세요.";
  }
  const blocks = sections.map((s) => {
    const lines = s.items
      .map((it) => ({ text: plainInline(it.text), note: plainInline(it.note ?? "") }))
      .filter((it) => it.text)
      .map((it) => `- ${it.text}${it.note ? ` — ${it.note}` : ""}`);
    return [`**${plainInline(s.title) || s.key}**`, ...lines].join("\n");
  });
  return ["이렇게 물어볼 수 있습니다.", ...blocks].join("\n\n");
}
